import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { NAV_LINKS, openModal, scrollToId } from "./data";
import { EASE, Magnetic } from "./ui-bits";

const Navbar = ({ ready }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-[100] transition-colors duration-500 ${
          scrolled
            ? "border-b border-ivory/10 bg-obsidian/80 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
        initial={{ y: -80, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 md:h-20 md:px-10">
          <button
            type="button"
            onClick={() => scrollToId("#top")}
            className="flex items-center gap-3"
            data-testid="nav-logo"
          >
            <img
              src="/assets/logo-light.png"
              alt="Elevate Estates"
              className="h-7 w-7 object-contain"
            />
            <span className="text-[12px] font-medium uppercase tracking-[0.34em] text-ivory">
              Elevate Estates
            </span>
          </button>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                type="button"
                onClick={() => go(l.href)}
                className="group relative text-[12px] font-medium uppercase tracking-[0.18em] text-ivory/70 transition-colors duration-300 hover:text-ivory"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-champagne transition-transform duration-500 group-hover:origin-left group-hover:scale-x-100" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Magnetic>
              <button
                type="button"
                onClick={() => openModal("find")}
                className="hidden items-center gap-2 border border-ivory/25 px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:border-champagne hover:text-champagne sm:inline-flex"
                data-testid="nav-find-space-btn"
              >
                Find a Space <ArrowUpRight size={13} />
              </button>
            </Magnetic>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
              data-testid="nav-menu-btn"
              aria-label="Open menu"
            >
              <span className="h-px w-6 bg-ivory" />
              <span className="h-px w-6 bg-ivory" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[150] flex flex-col bg-obsidian"
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <div className="flex h-16 items-center justify-between px-5">
              <span className="text-[12px] font-medium uppercase tracking-[0.34em] text-ivory">
                Elevate Estates
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[11px] uppercase tracking-[0.28em] text-stone"
                data-testid="mobile-menu-close"
              >
                Close
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6">
              {NAV_LINKS.map((l, i) => (
                <div key={l.href} className="overflow-hidden">
                  <motion.button
                    type="button"
                    onClick={() => go(l.href)}
                    className="block py-2 font-serif text-4xl text-ivory"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.6, delay: 0.15 + i * 0.06, ease: EASE }}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </motion.button>
                </div>
              ))}
            </nav>
            <motion.div
              className="flex flex-col gap-3 px-6 pb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.6, ease: EASE }}
            >
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openModal("find");
                }}
                className="w-full bg-ivory py-4 text-[12px] font-medium uppercase tracking-[0.24em] text-obsidian"
                data-testid="mobile-find-space-btn"
              >
                Find a Space
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openModal("list");
                }}
                className="w-full border border-ivory/25 py-4 text-[12px] font-medium uppercase tracking-[0.24em] text-ivory"
                data-testid="mobile-list-property-btn"
              >
                List Your Property
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
